/**
 * Value handling shared by the lookups and the outputs. Kept apart from
 * firestore-lookup.js so it carries no Firebase import and can be reasoned about
 * (and tested) on its own.
 */

/**
 * Turn a Firestore timestamp, a JS date or a string into an ISO string. Returns
 * an empty string for anything missing so report cells stay blank rather than
 * showing "Invalid date".
 */
export const toISO = (value) => {
	if (!value) return ""

	if (typeof value === "object" && typeof value.seconds === "number") {
		return new Date(value.seconds * 1000).toISOString()
	}

	if (value instanceof Date) return value.toISOString()

	if (typeof value === "string") {
		const parsed = new Date(value)

		return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString()
	}

	return ""
}

/**
 * The value of a booking to the business: the total holiday cost shown on the
 * booking's summary tab, stored as `margins.customer_price`.
 *
 * Where a booking carries no customer price we fall back to the figure quoted on
 * the originating enquiry, which is a different thing and so is recorded as such.
 * Every booking row names the field its figure came from, so every total in the
 * report can be traced back to a field.
 *
 * A stored zero counts as a real figure, not a missing one.
 */
export const resolveCustomerPrice = (booking) => {
	const candidates = [
		{ source: "margins.customer_price", value: booking?.margins?.customer_price },
		{ source: "enquiry.quoted", value: booking?.enquiry?.quoted },
	]

	for (const candidate of candidates) {
		const numeric = Number(candidate.value)

		if (candidate.value !== undefined && candidate.value !== null && candidate.value !== "" && !Number.isNaN(numeric)) {
			return { value: numeric, source: candidate.source }
		}
	}

	return { value: 0, source: "none" }
}
